import React, { useState } from 'react';
import { Menu, Bell, Sun, Moon, Search, X, User, FileText, CreditCard, Building, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Logo } from '../ui/logo';
import { useTheme } from '../theme/use-theme';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { api } from '../../lib/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStandort } from './use-standort';
import { Switch } from '../ui/switch';
import { personDetailPath, subprofileDetailPath } from '../../lib/peopleRoutes';
import { relationshipText } from '../../lib/subprofileRelationships';

interface HeaderProps {
  onMenuClick: () => void;
}
type SearchResultItem = {
  type: string;
  id: string;
  name: string;
  detail: string;
  path: string;
  relationshipText?: string;
  isTeacher?: boolean;
  isMainProfile?: boolean;
  ageText?: string | null;
  standorte?: Array<{ id: number; shortCode: string; name?: string }>;
};

export const Header = ({
  onMenuClick
}: HeaderProps) => {
  const {
    theme,
    setTheme
  } = useTheme();
  const { selectedStandortId, selectedStandort, standorte, setSelectedStandortId } = useStandort();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchAllStandorte, setSearchAllStandorte] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState<number>(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const [debounceTimer, setDebounceTimer] = useState<any>(null);
  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (query.length < 2) {
      setSearchResults([]);
      setActiveSearchIndex(-1);
      setSearchLoading(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('limit', '10');
        if (searchAllStandorte) {
          params.set('all_standorte', '1');
        } else if (selectedStandortId) {
          params.set('standort_id', String(selectedStandortId));
        }
        const list = await api.get<any[]>(`/api/personen?${params.toString()}`);
        const toAgeText = (birthDate?: string | null): string | null => {
          if (!birthDate) return null;
          const birth = new Date(birthDate);
          if (Number.isNaN(birth.getTime())) return null;
          const now = new Date();
          let years = now.getFullYear() - birth.getFullYear();
          const m = now.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) {
            years -= 1;
          }
          return years >= 0 ? `${years} J.` : null;
        };
        const mapped = (list || []).map(p => {
          const isSub = p.isSubprofile === true;
          const id = String(p.id ?? p.wpUserId ?? p.ID ?? '');
          const parentId = p.parentId ? String(p.parentId) : undefined;
          const subIndex = Number(p.subIndex || id.split('-').at(-1) || 1);
          if (isSub && subIndex <= 1) {
            return null;
          }
          let path = personDetailPath(id, location.pathname);
          if (isSub && parentId) {
            path = subprofileDetailPath(parentId, subIndex, location.pathname);
          }
          const relText = p.relationshipText || relationshipText(p.relationshipType, p.parentName);
          const roleNames: string[] = Array.isArray(p.roles) ? p.roles.map((r: string) => String(r)) : [];
          const isTeacher = roleNames.includes('teacher') || p.type === 'teacher' || p.type === 'both';
          const isMainProfile = !isSub;
          const standorte = Array.isArray(p.standorte)
            ? p.standorte.map((s: any) => ({
                id: Number(s.id || 0),
                shortCode: String(s.shortCode || '').trim().toUpperCase(),
                name: String(s.name || ''),
              })).filter((s: { id: number; shortCode: string }) => s.id > 0 && s.shortCode)
            : [];
          return ({
            type: isSub ? 'subprofile' : 'person',
            id,
            name: p.name ?? p.display_name ?? '',
            detail: (() => {
              if (isSub) return relText || 'Schülerprofil';
              return 'Hauptprofil';
            })(),
            path,
            relationshipText: relText || undefined,
            isTeacher,
            isMainProfile,
            ageText: toAgeText(p.birthDate),
            standorte,
          });
        }).filter((x): x is SearchResultItem => Boolean(x && x.id && x.name));
        setSearchResults(mapped);
        setActiveSearchIndex(mapped.length > 0 ? 0 : -1);
      } catch (e) {
        console.warn('Autocomplete fehlgeschlagen', e);
        setSearchResults([]);
        setActiveSearchIndex(-1);
      } finally {
        setSearchLoading(false);
      }
    }, 200);
    setDebounceTimer(t);
  };
  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setActiveSearchIndex(-1);
    setSearchLoading(false);
  };
  const handleLocationChange = (locationId: number) => {
    setSelectedStandortId(locationId);
  };
  
  const getIconForResultType = (type: string) => {
    switch (type) {
      case 'person':
        return <User className="h-4 w-4 text-muted-foreground" />;
      case 'subprofile':
        return <User className="h-4 w-4 text-violet-500" />;
      case 'contract':
        return <FileText className="h-4 w-4 text-muted-foreground" />;
      case 'mandate':
      case 'invoice':
        return <CreditCard className="h-4 w-4 text-muted-foreground" />;
      default:
        return <FileText className="h-4 w-4 text-muted-foreground" />;
    }
  };
  
  const getCurrentLocationName = () => {
    return selectedStandort?.name || 'Standort wählen';
  };
  return <header className="chrome-glass relative z-40 border-b">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={onMenuClick} aria-label="Menü öffnen">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="md:hidden">
            <Logo size="sm" showText={false} />
          </div>
          {/* Location Selector */}
          <div className="hidden md:flex">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-primary" />
                  <span className="max-w-[180px] truncate">
                    {getCurrentLocationName()}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[240px]">
                <DropdownMenuLabel>Standort wechseln</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* Der gewählte Standort trägt die CI-Farbe, nicht die grüne Akzentfarbe —
                    wie die aktive Seite im Menü. */}
                {standorte.map(location => <DropdownMenuItem key={location.id} className={selectedStandortId === location.id ? 'bg-primary/12 font-medium text-foreground' : ''} onClick={() => handleLocationChange(location.id)}>
                    <Building className="mr-2 h-4 w-4 text-primary" />
                    {location.name}
                  </DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile Location Selector */}
          <div className="md:hidden">
              <Select 
                // Leerer Text statt undefined: sonst wechselt das Feld beim Laden der
                // Standorte von „ungesteuert" zu „gesteuert" — diese React-Warnung stand
                // in fast jedem Ticket im Konsolenauszug (T-0140).
                value={selectedStandortId ? String(selectedStandortId) : ''}
                onValueChange={(val) => handleLocationChange(Number(val))}
              >
              <SelectTrigger className="w-11 px-2">
                <Building className="h-5 w-5 text-primary" />
              </SelectTrigger>
              <SelectContent>
                {standorte.map(location => <SelectItem key={location.id} value={String(location.id)}>
                    {location.name}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Suchleiste */}
          <div className="relative z-50">
            {searchOpen ? <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input type="search" placeholder="Name, BS-12345, P-…, Kürzel" className="w-full pl-10 pr-40 md:w-80 lg:w-96" value={searchQuery} onChange={handleSearchChange} autoFocus role="combobox" aria-expanded={searchResults.length > 0} aria-controls="header-search-results" aria-activedescendant={activeSearchIndex >= 0 ? `header-search-result-${activeSearchIndex}` : undefined} onKeyDown={(ev) => {
                  if (ev.key === 'ArrowDown' && searchResults.length > 0) {
                    ev.preventDefault();
                    setActiveSearchIndex((prev) => Math.min(searchResults.length - 1, prev + 1));
                    return;
                  }
                  if (ev.key === 'ArrowUp' && searchResults.length > 0) {
                    ev.preventDefault();
                    setActiveSearchIndex((prev) => Math.max(0, prev - 1));
                    return;
                  }
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    if (activeSearchIndex >= 0 && searchResults[activeSearchIndex]) {
                      navigate(searchResults[activeSearchIndex].path);
                      setSearchOpen(false);
                      return;
                    }
                    const s = searchQuery.trim();
                    if (s.length > 0) {
                      const basePath = location.pathname.startsWith('/system') ? '/system/dashboard/search' : '/dashboard/search';
                      const params = new URLSearchParams();
                      params.set('q', s);
                      if (searchAllStandorte) {
                        params.set('all_standorte', '1');
                      } else if (selectedStandortId) {
                        params.set('standort_id', String(selectedStandortId));
                      }
                      navigate({ pathname: basePath, search: params.toString() });
                      setSearchOpen(false);
                    }
                  }
                  if (ev.key === 'Escape') {
                    setSearchOpen(false);
                    setActiveSearchIndex(-1);
                  }
                }} />
                <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-full border border-border/70 bg-background/95 px-2 py-1 text-[10px] text-muted-foreground">
                  <span>Alle Standorte</span>
                  <Switch checked={searchAllStandorte} onCheckedChange={setSearchAllStandorte} className="scale-75" />
                  <button type="button" onClick={handleSearchClear} className="pl-1">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="sr-only" aria-live="polite">
                  {searchAllStandorte ? 'Suche in allen freigegebenen Standorten aktiv' : 'Suche im aktuellen Standort aktiv'}
                </div>
                {/* Suchergebnisse Dropdown */}
      {searchResults.length > 0 ? (
                  <div id="header-search-results" className="absolute z-[60] mt-1 w-full rounded-md border border-border/70 bg-background/95 p-1 shadow-surface motion-safe:animate-fade-up" aria-label="Suchergebnisse">
                    <div className="py-1">
                      {searchResults.map((result, idx) => (
                        <button key={`${result.type}-${result.id}`} id={`header-search-result-${idx}`} className={`flex w-full items-center rounded-md px-3 py-2 text-left transition-colors hover:bg-muted ${activeSearchIndex === idx ? 'bg-muted' : ''}`} onMouseEnter={() => setActiveSearchIndex(idx)} onClick={() => {
                          navigate(result.path);
                          setSearchOpen(false);
                        }}>
                          <div className="mr-2">
                            {getIconForResultType(result.type)}
                          </div>
                          <div className="flex flex-1 items-start justify-between gap-3">
                            <div>
                              <div className="font-medium">{result.name}</div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
                                <span className="text-muted-foreground">ID: {result.id}</span>
                                {result.ageText ? <span className="text-muted-foreground">• {result.ageText}</span> : null}
                                {result.isMainProfile ? (
                                  <span className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-slate-700">Hauptprofil</span>
                                ) : null}
                                {result.type === 'subprofile' ? (
                                  <span className="rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-violet-700">Schülerprofil</span>
                                ) : null}
                                {result.isTeacher ? (
                                  <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-emerald-700">Lehrkraft</span>
                                ) : null}
                              </div>
                              <div className="text-xs text-muted-foreground">{result.detail}</div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-1">
                              {(result.standorte || []).map((s) => (
                                <span key={`${result.id}-sid-${s.id}`} title={s.name || ''} className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                                  {s.shortCode}
                                </span>
                              ))}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
      ) : searchQuery.trim().length >= 2 && !searchLoading ? (
                  <div id="header-search-results" className="absolute z-[60] mt-1 w-full rounded-md border border-border/70 bg-background/95 p-3 text-sm text-muted-foreground shadow-surface">
                    Keine Ergebnisse für "{searchQuery}".
                  </div>
      ) : null}
              </div> : <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Suche öffnen">
                <Search className="h-5 w-5" />
              </Button>}
          </div>
          <Button variant="ghost" size="icon" aria-label="Benachrichtigungen">
            <Bell className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Hellmodus aktivieren' : 'Dunkelmodus aktivieren'}>
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </div>
    </header>;
};